import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { X, Trash, Printer, ShareNetwork } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { GroupPicker } from "@/components/GroupPicker";
import { useFieldCfg } from "@/lib/softwareConfig";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtINR = (n) => {
  if (!n || Number(n) === 0) return "";
  return Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

function VoucherForm({ vchType, editId }) {
  const fcfg = useFieldCfg("voucher");
  const [parties, setParties] = useState([]);
  const [party, setParty] = useState("");
  const [query, setQuery] = useState("");
  const [showList, setShowList] = useState(false);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [account, setAccount] = useState("Cash");
  const [reference, setReference] = useState("");
  const [invoiceNo, setInvoiceNo] = useState("");
  const [bills, setBills] = useState([]);
  const [groups, setGroups] = useState([]);
  const [groupId, setGroupId] = useState("");
  const [groupName, setGroupName] = useState("");
  const [amount, setAmount] = useState("");
  const [narration, setNarration] = useState("");
  const [recent, setRecent] = useState([]);
  const [newParty, setNewParty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);

  const loadParties = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/voucher-parties`, { params: { vch_type: vchType } });
      setParties(res.data.parties || []);
    } catch {}
  }, [vchType]);

  const loadRecent = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/vouchers`, { params: { vch_type: vchType } });
      setRecent((res.data.vouchers || []).slice(0, 8));
    } catch {}
  }, [vchType]);

  useEffect(() => { loadParties(); loadRecent(); }, [loadParties, loadRecent]);

  const startEdit = useCallback((v) => {
    setEditing(v.id);
    setParty(v.party_name || ""); setQuery(""); setNewParty(false);
    setDate(v.date || new Date().toISOString().slice(0, 10));
    setAccount(v.account || "Cash");
    setReference(v.reference || "");
    setInvoiceNo(v.invoice_no || "");
    setAmount(String(v.amount || ""));
    setNarration(v.narration || "");
    setGroupName(v.under_group || "");
  }, []);

  useEffect(() => {
    if (!editId) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/vouchers`, { params: { vch_type: vchType } });
        const v = (res.data.vouchers || []).find((x) => x.id === editId);
        if (v) startEdit(v);
      } catch {}
    })();
  }, [editId, vchType, startEdit]);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/account-groups`);
        setGroups(res.data.groups || []);
      } catch {}
    })();
  }, []);

  useEffect(() => {
    setBills([]); setInvoiceNo("");
    if (!party) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/voucher-party-bills`, { params: { vch_type: vchType, party } });
        setBills(res.data.bills || []);
      } catch {}
    })();
  }, [party, vchType]);

  const filtered = parties.filter((p) => p.name.toLowerCase().includes(query.toLowerCase()));
  const selectedInfo = parties.find((p) => p.name === party);

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        vch_type: vchType, date, party_name: newParty ? query.trim() : party,
        account, reference, invoice_no: invoiceNo, under_group: groupName,
        amount: parseFloat(amount || 0), narration,
      };
      if (editing) await axios.put(`${API}/vouchers/${editing}`, payload);
      else await axios.post(`${API}/vouchers`, payload);
      toast.success(`${vchType === "receipt" ? "Receipt" : "Payment"} ${editing ? "updated" : "saved"}`);
      setEditing(null);
      setAmount(""); setReference(""); setNarration(""); setParty(""); setQuery(""); setNewParty(false); setInvoiceNo(""); setGroupId(""); setGroupName("");
      loadParties(); loadRecent();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(false);
  };

  const del = async (v) => {
    if (!window.confirm(`Delete ${v.vch_no}?`)) return;
    try {
      await axios.delete(`${API}/vouchers/${v.id}`);
      toast.success("Deleted");
      loadParties(); loadRecent();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const openPdf = async (v) => {
    try {
      const res = await axios.get(`${API}/vouchers/${v.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch { toast.error("Failed to generate PDF"); }
  };

  const sharePdf = async (v) => {
    try {
      const res = await axios.get(`${API}/vouchers/${v.id}/pdf`, { responseType: "blob" });
      const file = new File([res.data], `${v.vch_no}.pdf`, { type: "application/pdf" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: v.vch_no });
      } else {
        const url = URL.createObjectURL(file);
        const a = document.createElement("a");
        a.href = url; a.download = `${v.vch_no}.pdf`; a.click();
        toast.success("PDF downloaded — you can send it from your device");
      }
    } catch { toast.error("Failed to share"); }
  };

  const label = vchType === "receipt" ? "Received From (Sales Party)" : "Paid To (Purchase Party)";

  return (
    <div className="p-4" data-testid={`voucher-form-${vchType}`}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="relative">
          <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-600 mb-1">{label}</label>
          <input
            value={party || query}
            onChange={(e) => { setQuery(e.target.value); setParty(""); setShowList(true); setNewParty(false); }}
            onFocus={() => setShowList(true)}
            placeholder="Type party name…"
            data-testid={`vch-party-input-${vchType}`}
            className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white"
          />
          {showList && !party && (
            <div className="absolute z-20 mt-1 w-full bg-white border border-slate-300 rounded-sm shadow-lg max-h-56 overflow-y-auto" data-testid={`vch-party-list-${vchType}`}>
              {filtered.map((p) => (
                <button key={p.name}
                  onClick={() => { setParty(p.name); setQuery(""); setShowList(false); }}
                  data-testid={`vch-party-opt-${p.name}`}
                  className="w-full text-left px-3 py-1.5 text-sm hover:bg-teal-50 flex justify-between gap-2">
                  <span className="font-semibold">{p.name}</span>
                  <span className="text-xs text-slate-500">Bal: {fmtINR(p.outstanding) || "0"}</span>
                </button>
              ))}
              {query.trim() && !filtered.some((p) => p.name.toLowerCase() === query.trim().toLowerCase()) && (
                <button onClick={() => { setNewParty(true); setParty(""); setShowList(false); }}
                  data-testid={`vch-party-create-${vchType}`}
                  className="w-full text-left px-3 py-1.5 text-sm font-bold text-teal-700 hover:bg-teal-50">
                  + Use new party "{query.trim()}"
                </button>
              )}
              {filtered.length === 0 && !query.trim() && (
                <div className="px-3 py-2 text-xs text-slate-500">No parties yet</div>
              )}
            </div>
          )}
          {selectedInfo && (
            <div className="mt-1 text-[11px] text-slate-600" data-testid={`vch-party-outstanding-${vchType}`}>
              Billed: ₹{fmtINR(selectedInfo.billed) || "0"} · Settled: ₹{fmtINR(selectedInfo.settled) || "0"} ·
              <span className="font-bold text-slate-900"> Outstanding: ₹{fmtINR(selectedInfo.outstanding) || "0"}</span>
            </div>
          )}
          {newParty && <div className="mt-1 text-[11px] font-bold text-teal-700">New party: {query.trim()}</div>}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-600 mb-1">Date</label>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
              data-testid={`vch-date-${vchType}`}
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white" />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-600 mb-1">Account</label>
            <select value={account} onChange={(e) => setAccount(e.target.value)}
              data-testid={`vch-account-${vchType}`}
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white">
              <option>Cash</option>
              <option>Bank</option>
              <option>Cheque</option>
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-600 mb-1">{fcfg.label("amount", "Amount (₹)")}</label>
            <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00" data-testid={`vch-amount-${vchType}`} disabled={!fcfg.canEdit("amount")}
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white disabled:bg-slate-100 disabled:text-slate-400" />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-600 mb-1">Under Group</label>
            <GroupPicker
              groups={groups}
              value={groupId}
              onChange={(g) => { setGroupId(g.id); setGroupName(g.name); }}
              onGroupCreated={(g) => setGroups((gs) => [...gs, g])}
              testid={`vch-group-${vchType}`}
            />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-600 mb-1">Against Invoice No.</label>
            {bills.length > 0 ? (
              <select value={invoiceNo} onChange={(e) => setInvoiceNo(e.target.value)}
                data-testid={`vch-invoice-${vchType}`}
                className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white">
                <option value="">— Select bill —</option>
                {bills.map((b) => (
                  <option key={b.no} value={b.no}>{b.no} · {b.date} · ₹{fmtINR(b.total)}</option>
                ))}
              </select>
            ) : (
              <input value={invoiceNo} onChange={(e) => setInvoiceNo(e.target.value)}
                placeholder="Optional" data-testid={`vch-invoice-input-${vchType}`}
                className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white" />
            )}
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-600 mb-1">Reference (Cheque/UTR No.)</label>
            <input value={reference} onChange={(e) => setReference(e.target.value)}
              placeholder="Optional" data-testid={`vch-reference-${vchType}`}
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white" />
          </div>
        </div>
      </div>
      <div className="mt-3 flex gap-3 items-end">
        <div className="flex-1">
          <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-600 mb-1">{fcfg.label("narration", "Narration")}</label>
          <input value={narration} onChange={(e) => setNarration(e.target.value)}
            placeholder="Optional note" data-testid={`vch-narration-${vchType}`} disabled={!fcfg.canEdit("narration")}
            className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white disabled:bg-slate-100 disabled:text-slate-400" />
        </div>
        <button onClick={save} disabled={busy || (!party && !(newParty && query.trim())) || !amount}
          data-testid={`vch-save-${vchType}`}
          className="px-6 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-sm text-sm font-bold disabled:opacity-50">
          {editing ? "Update" : "Save"} {vchType === "receipt" ? "Receipt" : "Payment"}
        </button>
        {editing && (
          <button onClick={() => { setEditing(null); setAmount(""); setReference(""); setNarration(""); setParty(""); setQuery(""); setInvoiceNo(""); setGroupName(""); }}
            data-testid={`vch-cancel-edit-${vchType}`}
            className="px-4 py-2 border border-slate-400 text-slate-700 rounded-sm text-sm font-bold hover:bg-white">
            Cancel
          </button>
        )}
      </div>

      {recent.length > 0 && (
        <div className="mt-4 border-t border-slate-200 pt-2" data-testid={`vch-recent-${vchType}`}>
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Recent entries</div>
          {recent.map((v) => (
            <div key={v.id} className="flex items-center gap-2 text-sm py-0.5 hover:bg-[#f1e9d6] px-2 rounded-sm" data-testid={`vch-row-${v.vch_no}`}>
              <span className="w-24 text-xs text-slate-500">{v.date}</span>
              <span className="w-24 text-xs font-bold">{v.vch_no}</span>
              <span className="flex-1 font-semibold cursor-pointer hover:underline" onClick={() => startEdit(v)} data-testid={`vch-edit-${v.vch_no}`}>{v.party_name}</span>
              <span className="w-16 text-xs text-slate-500">{v.account}</span>
              <span className="w-28 text-right font-bold">₹{fmtINR(v.amount)}</span>
              <button onClick={() => openPdf(v)} data-testid={`vch-print-${v.vch_no}`} title="Print PDF"
                className="p-1 text-teal-600 hover:text-teal-800"><Printer size={15} /></button>
              <button onClick={() => sharePdf(v)} data-testid={`vch-share-${v.vch_no}`} title="Send / Share"
                className="p-1 text-blue-500 hover:text-blue-700"><ShareNetwork size={15} /></button>
              <button onClick={() => del(v)} data-testid={`vch-del-${v.vch_no}`}
                className="p-1 text-red-400 hover:text-red-600"><Trash size={14} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function VoucherView({ onClose, initialMode = null, editId = "" }) {
  const { user } = useAuth();
  const [mode, setMode] = useState(initialMode); // null = menu, "receipt" | "payment" = full entry window
  const [selected, setSelected] = useState(0);
  const company = user?.company_name || "";
  const OPTIONS = ["receipt", "payment"];

  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target.tagName || "").toLowerCase();
      if (e.key === "Escape") {
        e.preventDefault(); e.stopPropagation();
        if (mode) setMode(null); else onClose();
        return;
      }
      if (mode || tag === "input" || tag === "select" || tag === "textarea") return;
      if (e.key === "ArrowDown") { e.preventDefault(); setSelected((s) => Math.min(s + 1, 1)); }
      else if (e.key === "ArrowUp") { e.preventDefault(); setSelected((s) => Math.max(s - 1, 0)); }
      else if (e.key === "Enter") { e.preventDefault(); setMode(OPTIONS[selected]); }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onClose, mode, selected]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Full entry window (own screen, like billing) ----
  if (mode) {
    return (
      <div className="w-full" data-testid={`voucher-window-${mode}`}>
        <div className="w-full overflow-hidden shadow-xl flex flex-col" style={{ background: "#fbf7ee", border: "1px solid #e7dcc3", minHeight: "calc(100vh - 0.5rem)" }}>
          <div className="flex items-center justify-between px-3 py-1.5 text-white" style={{ background: "#0f766e" }}>
            <span className="text-sm font-bold" data-testid="vch-window-title">
              {mode === "receipt" ? "Receipt Voucher" : "Payment Voucher"}
            </span>
            <span className="text-sm font-black uppercase tracking-wide">{company}</span>
            <button onClick={() => setMode(null)} data-testid="vch-window-close" className="p-1 mr-12 hover:bg-white/20 rounded-sm">
              <X size={16} weight="bold" />
            </button>
          </div>
          <VoucherForm vchType={mode} editId={mode === initialMode ? editId : ""} />
        </div>
      </div>
    );
  }

  // ---- Menu: two options one below the other (Display Menu style) ----
  return (
    <div className="flex justify-center items-start pt-6 pb-10 min-h-[70vh]" data-testid="voucher-view">
      <div className="w-full max-w-md rounded-md overflow-hidden shadow-xl" style={{ background: "#fbf7ee", border: "1px solid #e7dcc3" }}>
        <div className="text-white text-center py-2 text-sm font-bold tracking-widest uppercase" style={{ background: "#0f766e" }} data-testid="vch-title">
          Voucher
        </div>
        <div className="py-8 px-10 select-none">
          <button onClick={() => setMode("receipt")} onMouseEnter={() => setSelected(0)}
            data-testid="vch-tab-receipt"
            className={`w-full text-left px-3 py-1 text-[15px] font-semibold rounded-sm transition-colors ${selected === 0 ? "bg-teal-600 text-white shadow-sm" : "text-slate-800 hover:bg-[#f1e9d6]"}`}>
            <span className="text-red-600 font-bold">R</span>eceipt
          </button>
          <div className="mt-2">
            <button onClick={() => setMode("payment")} onMouseEnter={() => setSelected(1)}
              data-testid="vch-tab-payment"
              className={`w-full text-left px-3 py-1 text-[15px] font-semibold rounded-sm transition-colors ${selected === 1 ? "bg-teal-600 text-white shadow-sm" : "text-slate-800 hover:bg-[#f1e9d6]"}`}>
              <span className="text-red-600 font-bold">P</span>ayment
            </button>
          </div>
        </div>
        <div className="h-1.5" style={{ background: "#0f766e" }} />
      </div>
    </div>
  );
}
